import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "typeSelector", "container", "input", "selectedSubjectables"]
  static values = { models: String, single: Boolean }

  connect() {
    // Überprüfe, ob alle erforderlichen Targets existieren
    if (!this.hasSearchInputTarget) {
      return;
    }
    
    if (!this.hasSearchResultsTarget) {
      return;
    }
    
    // Initialize selectedType
    this.selectedType = null;
    
    // Setup components
    this.setupTypeSelector();
    this.updateTypeSelector();
    this.setupSearchInput();
    
    // Add a small delay to ensure DOM is fully rendered
    setTimeout(() => {
      this.loadExistingSubjectables();
    }, 100);
  }

  // Test-Methode zum manuellen Setzen des Types
  testSetType(type) {
    this.selectedType = type;
    this.updateTypeSelector();
    
    // Teste auch die Suche
    if (this.hasSearchInputTarget && this.searchInputTarget.value.length >= 2) {
      this.performSearch(this.searchInputTarget.value);
    }
  }

  setupTypeSelector() {
    if (!this.hasTypeSelectorTarget) {
      return;
    }
    
    const buttons = this.typeSelectorTarget.querySelectorAll('button[data-type]');
    
    buttons.forEach((btn, index) => {
      const type = btn.dataset.type;
      
      // Remove existing event listeners to prevent duplicates
      btn.removeEventListener('click', btn._typeClickHandler);
      
      // Create new event handler
      btn._typeClickHandler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const clickedType = btn.dataset.type;
        
        if (clickedType === 'all') {
          // "Alle" Button - setze selectedType auf null
          this.selectedType = null;
        } else if (this.selectedType === clickedType) {
          this.selectedType = null; // Filter aufheben
        } else {
          this.selectedType = clickedType;
        }
        
        this.updateTypeSelector();
        this.clearSearch();
      };
      
      // Add event listener
      btn.addEventListener('click', btn._typeClickHandler);
    });
  }

  setupSearchInput() {
    if (!this.hasSearchInputTarget) {
      return;
    }
    
    // Debounce für die Suche
    let searchTimeout;
    this.searchInputTarget.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        this.performSearch(e.target.value);
      }, 300);
    });

    // Fokus-Events
    this.searchInputTarget.addEventListener('focus', () => {
      if (this.searchInputTarget.value.length >= 1) {
        this.performSearch(this.searchInputTarget.value);
      }
    });

    this.searchInputTarget.addEventListener('blur', () => {
      // Verzögerung, damit Klicks auf Ergebnisse funktionieren
      setTimeout(() => {
        this.hideResults();
      }, 200);
    });
  }

  updateTypeSelector() {
    if (!this.hasTypeSelectorTarget) {
      return;
    }
    
    const buttons = this.typeSelectorTarget.querySelectorAll('button[data-type]');
    
    buttons.forEach((btn, index) => {
      const buttonType = btn.dataset.type;
      
      if (buttonType === 'all') {
        // "Alle" Button ist aktiviert, wenn kein Type ausgewählt ist
        if (this.selectedType === null) {
          // Aktiviere den "Alle" Button (blau)
          btn.classList.remove('bg-gray-100', 'text-gray-700', 'border-gray-200');
          btn.classList.add('bg-blue-600', 'text-white', 'border-blue-600');
        } else {
          // Deaktiviere den "Alle" Button (grau)
          btn.classList.remove('bg-blue-600', 'text-white', 'border-blue-600');
          btn.classList.add('bg-gray-100', 'text-gray-700', 'border-gray-200');
        }
      } else if (this.selectedType === buttonType) {
        // Aktiviere den Button (blau)
        btn.classList.remove('bg-gray-100', 'text-gray-700', 'border-gray-200');
        btn.classList.add('bg-blue-600', 'text-white', 'border-blue-600');
      } else {
        // Deaktiviere den Button (grau)
        btn.classList.remove('bg-blue-600', 'text-white', 'border-blue-600');
        btn.classList.add('bg-gray-100', 'text-gray-700', 'border-gray-200');
      }
    });
  }

  performSearch(query) {
    if (!query || query.length < 1) {
      this.hideResults();
      return;
    }

    // Verwende die neue subjectable_search API mit Cache-Busting
    // Wenn ein Typ ausgewählt ist, suche nur in diesem Typ
    let url = `/backend/subjectable_search?q=${encodeURIComponent(query)}&_t=${Date.now()}`;
    if (this.selectedType) {
      url += `&type=${this.selectedType}`;
    }
    
    fetch(url, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(r => {
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
        return r.json();
      })
      .then(results => {
        if (!Array.isArray(results)) {
          this.hideResults();
          return;
        }
        
        this.displayResults(results);
      })
      .catch(error => {
        this.hideResults();
      });
  }

  displayResults(results) {
    if (!this.hasSearchResultsTarget) {
      return;
    }

    if (results.length === 0) {
      this.searchResultsTarget.innerHTML = '<div class="p-3 text-sm text-gray-500">Keine Ergebnisse gefunden</div>';
    } else {
      const resultsHtml = results.map((result) => {
        const typeLabel = this.getTypeLabel(result.type);
        const typeColor = this.getTypeColor(result.type);
        const displayName = result.display_name || result.name || result.text || 'Unknown';
        
        // Escape das JSON für das data-result Attribut
        const escapedResult = JSON.stringify(result).replace(/"/g, '&quot;');
        
        return `
          <div class="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" 
               data-action="click->subjectable-search#selectResult"
               data-result="${escapedResult}">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <span class="text-sm font-medium text-gray-900">${displayName}</span>
              </div>
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeColor}">
                ${typeLabel}
              </span>
            </div>
          </div>
        `;
      }).join('');
      
      this.searchResultsTarget.innerHTML = resultsHtml;
    }
    
    this.showResults();
  }

  decodeHtmlEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  getTypeLabel(type) {
    switch(type) {
      case 'Customer': return 'Kunde';
      case 'Contact': return 'Kontakt';
      case 'Requisition': return 'Stelle';
      case 'Quote': return 'Angebot';
      case 'Applicant': return 'Bewerber';
      case 'Profile': return 'Profil';
      case 'Talent': return 'Talent';
      default: return type;
    }
  }

  getTypeColor(type) {
    switch(type) {
      case 'Customer': return 'bg-blue-100 text-blue-800';
      case 'Contact': return 'bg-green-100 text-green-800';
      case 'Requisition': return 'bg-purple-100 text-purple-800';
      case 'Quote': return 'bg-yellow-100 text-yellow-800';
      case 'Applicant': return 'bg-orange-100 text-orange-800';
      case 'Profile': return 'bg-indigo-100 text-indigo-800';
      case 'Talent': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  showResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden');
    }
  }

  hideResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden');
    }
  }

  selectResult(event) {
    try {
      const resultString = event.currentTarget.dataset.result;
      
      // Unescape das JSON
      const unescapedResult = resultString.replace(/&quot;/g, '"');
      
      const resultData = JSON.parse(unescapedResult);
      
      // Setze den Wert im Suchfeld
      if (this.hasSearchInputTarget) {
        const displayName = resultData.display_name || resultData.name || resultData.text || 'Unknown';
        this.searchInputTarget.value = displayName;
      }
      
      // Hidden-Feld für (Multi-)Verknüpfungen
      const idsField = this.element.querySelector('input[name*="[subjectable_ids]"]');

      // Optionaler Single-Select-Modus: nur letzte Auswahl speichern,
      // ABER NIEMALS recipient_id anfassen (Verantwortung liegt bei spez. Controllern)
      const singleMode = this.element.hasAttribute('data-subjectable-search-single-value') ? true : false;

      if (idsField) {
        // Parse bestehende Einträge "id:type"
        let currentEntries = idsField.value ? idsField.value.split(',').map(e => e.trim()).filter(Boolean) : [];
        const newEntry = `${resultData.id}:${resultData.type}`;

        if (singleMode) {
          // Nur die aktuelle Auswahl behalten
          currentEntries = [newEntry];
        } else {
          if (!currentEntries.includes(newEntry)) {
            currentEntries.push(newEntry);
          }
        }

        idsField.value = currentEntries.join(',');

        // Anzeige aktualisieren
        const subjectablesArray = currentEntries.map(entry => {
          const [id, type] = entry.split(':');
          return { id: parseInt(id), type: type };
        });
        this.loadSubjectablesByIdsWithTypes(subjectablesArray);
      }
      
      // Verstecke die Ergebnisse
      this.hideResults();
      
      // Dispatch Custom Event für abhängige Controller (z. B. Phonecall, der selbst recipient_id setzt)
      const customEvent = new CustomEvent('subjectable-search:selected', {
        detail: resultData,
        bubbles: true
      })
      this.element.dispatchEvent(customEvent)
    } catch (error) {
      // Silent error handling
    }
  }

  selectType(event) {
    const type = event.currentTarget.dataset.type;
    if (this.selectedType === type) {
      this.selectedType = null; // Filter aufheben
    } else {
      this.selectedType = type;
    }
    this.updateTypeSelector();
    this.clearSearch();
  }

  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = '';
    }
    this.hideResults();
  }

  loadExistingSubjectables() {
    const idsField = this.element.querySelector('input[name*="[subjectable_ids]"]');
    
    if (idsField && idsField.value) {
      // Parse existing entries (format: "id:type,id:type")
      const entries = idsField.value.split(',').map(entry => entry.trim()).filter(entry => entry);
      
      if (entries.length > 0) {
        // Check if entries are in new format (id:type) or old format (just id)
        const hasTypeInfo = entries.some(entry => entry.includes(':'));
        
        if (hasTypeInfo) {
          // New format - convert to array of {id, type} objects
          const subjectablesArray = entries.map(entry => {
            const [id, type] = entry.split(':');
            return { id: parseInt(id), type: type };
          });
          this.loadSubjectablesByIdsWithTypes(subjectablesArray);
        } else {
          // Old format - but still use loadSubjectablesByIdsWithTypes to prevent loading all entities
          const subjectablesArray = entries.map(entry => {
            return { id: parseInt(entry), type: 'Unknown' };
          });
          this.loadSubjectablesByIdsWithTypes(subjectablesArray);
        }
      }
    }
  }

  loadSubjectablesByIdsWithTypes(entries) {
    // Parse entries in format "id:type" or accept array of {id, type} objects
    const subjectables = [];
    
    if (Array.isArray(entries) && entries.length > 0) {
      if (typeof entries[0] === 'string') {
        // String format: "id:type"
        entries.forEach(entry => {
          const [id, type] = entry.split(':');
          if (id && type) {
            subjectables.push({ id: parseInt(id), type: type });
          }
        });
      } else if (typeof entries[0] === 'object' && entries[0].id && entries[0].type) {
        // Object format: {id, type}
        subjectables.push(...entries);
      }
    }
    
    // Check if we have any 'Unknown' types (legacy format)
    const hasUnknownTypes = subjectables.some(s => s.type === 'Unknown');
    if (hasUnknownTypes) {
      const ids = subjectables.map(s => s.id);
      this.loadSubjectablesByIds(ids);
      return;
    }
    
    // Load each subjectable individually with its specific type
    const promises = subjectables.map(subjectable => {
      // Use the specific type to filter results - only fetch this specific type
      const url = `/backend/subjectable_search?ids=${subjectable.id}&type=${subjectable.type}`;
      
      return fetch(url)
        .then(r => r.json())
        .then(results => {
          // Filter results to only include the specific type
          const filteredResults = results.filter(result => result.type === subjectable.type);
          
          if (filteredResults.length > 0) {
            return filteredResults[0]; // Return the first (and should be only) result
          } else {
            return null;
          }
        })
        .catch(error => {
          return null;
        });
    });
    
    Promise.all(promises)
      .then(results => {
        const validResults = results.filter(result => result !== null);
        this.displaySelectedSubjectables(validResults);
      })
      .catch(error => {
        // Silent error handling
      });
  }

  loadSubjectablesByIds(ids, forceType = null) {
    let url = `/backend/subjectable_search?ids=${ids.join(',')}`;
    
    // Wenn forceType angegeben ist, verwende diesen (für spezifische Filterung)
    // Ansonsten lade alle IDs ohne Type-Filter (für Anzeige der ausgewählten Items)
    if (forceType) {
      url += `&type=${forceType}`;
    }
    
    fetch(url)
      .then(r => r.json())
      .then(subjectables => {
        this.displaySelectedSubjectables(subjectables);
      })
      .catch(error => {
        // Silent error handling
      });
  }

  displaySelectedSubjectables(subjectables) {
    // Verwende das selectedSubjectables Target
    if (!this.hasSelectedSubjectablesTarget) {
      return;
    }
    
    const container = this.selectedSubjectablesTarget;
    container.innerHTML = '';
    
    subjectables.forEach(subjectable => {
      const typeColor = this.getTypeColor(subjectable.type);
      const typeLabel = this.getTypeLabel(subjectable.type);
      const displayName = subjectable.display_name || subjectable.name || subjectable.text || 'Unknown';
      
      const tag = document.createElement('div');
      tag.className = 'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200';
      // Name klickbar (oeffnet den Bezug), × entfernt ihn — beides im selben Chip
      // (Wunsch Lorenz/Jochen 23.07.2026). target=_top verlaesst evtl. Turbo-Frames.
      const nameHtml = subjectable.url
        ? `<a href="${subjectable.url}" target="_top" class="hover:underline hover:text-blue-700" title="Bezug öffnen">${displayName}</a>`
        : `<span>${displayName}</span>`;
      tag.innerHTML = `
        <span class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${typeColor}">
          ${typeLabel}
        </span>
        ${nameHtml}
        <button type="button" class="ml-1 text-gray-400 hover:text-gray-600" data-action="click->subjectable-search#removeSubjectable" data-id="${subjectable.id}" data-type="${subjectable.type}" title="Bezug entfernen">
          <i class="fas fa-times"></i>
        </button>
      `;
      container.appendChild(tag);
    });
  }

  removeSubjectable(event) {
    const idToRemove = event.currentTarget.dataset.id;
    const typeToRemove = event.currentTarget.dataset.type;
    const idsField = this.element.querySelector('input[name*="[subjectable_ids]"]');
    
    // Prüfe ob es ein recipient_id Feld gibt (für Phonecalls)
    const recipientIdField = this.element.closest('form')?.querySelector('input[name*="[recipient_id]"]');
    
    if (recipientIdField && recipientIdField.value === idToRemove) {
      // Für Phonecalls: Leere recipient_id
      recipientIdField.value = ''
      
      // Leere auch das Suchfeld
      if (this.hasSearchInputTarget) {
        this.searchInputTarget.value = ''
      }
      
      // Leere die Anzeige
      if (this.hasSelectedSubjectablesTarget) {
        this.selectedSubjectablesTarget.innerHTML = ''
      }
      
      return
    }
    
    if (idsField) {
      const currentEntries = idsField.value ? idsField.value.split(',').map(entry => entry.trim()).filter(entry => entry) : [];
      
      // Create the entry to remove (either "id:type" or just "id" for backward compatibility)
      const entryToRemove = typeToRemove ? `${idToRemove}:${typeToRemove}` : idToRemove;
      
      const newEntries = currentEntries.filter(entry => entry !== entryToRemove);
      idsField.value = newEntries.join(',');
      
      // Aktualisiere die Anzeige
      if (newEntries.length > 0) {
        // Check if entries are in new format (id:type) or old format (just id)
        const hasTypeInfo = newEntries.some(entry => entry.includes(':'));
        
        if (hasTypeInfo) {
          // Convert to array of {id, type} objects
          const subjectablesArray = newEntries.map(entry => {
            const [id, type] = entry.split(':');
            return { id: parseInt(id), type: type };
          });
          this.loadSubjectablesByIdsWithTypes(subjectablesArray);
        } else {
          this.loadSubjectablesByIds(newEntries);
        }
      } else {
        const container = this.element.querySelector('.selected-subjectables');
        if (container) {
          container.remove();
        }
      }
    }
  }
}